import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_LANG, SUPPORTED_LANGS, isSupportedLang, saveStoredLang } from "./langs";
import { messages } from "./messages";
import { setLang as setLangAction } from "../features/LangSlice";

export { DEFAULT_LANG, SUPPORTED_LANGS };

// 컴포넌트 밖(유틸·토스트 함수)에서도 현재 언어를 알 수 있도록 모듈 변수로 들고 있는다.
// 값은 bindLangToStore 가 스토어 변경마다 맞춰 준다.
let currentLang = DEFAULT_LANG;

export const getLang = () => currentLang;

const interpolate = (template, params) => {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match
  );
};

// key 는 "네임스페이스.키" 꼴. 첫 점 앞이 messages/ 아래 파일 이름, 뒤가 그 파일 안의 키다.
// 키 안에 점이 더 있어도 된다("mission.upload.done" → mission 파일의 "upload.done").
// 값이 함수면 params 를 넘겨 호출한다(복수형·조건부 문구용).
export const translate = (lang, key, params) => {
  const dot = key.indexOf(".");
  const ns = dot === -1 ? "" : key.slice(0, dot);
  const rest = dot === -1 ? key : key.slice(dot + 1);
  const dict = messages[lang]?.[ns] ?? {};
  const fallback = messages[DEFAULT_LANG]?.[ns] ?? {};
  const value = dict[rest] ?? fallback[rest];
  if (value == null) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[i18n] missing key: ${key}`);
    }
    return key;
  }
  if (typeof value === "function") return value(params ?? {});
  return interpolate(value, params);
};

// 훅을 못 쓰는 곳(토스트 유틸, 슬라이스 밖 함수)용.
export const t = (key, params) => translate(currentLang, key, params);

// 테스트 스토어에 lang 리듀서가 없어도 기본 언어로 동작하도록 옵셔널 접근.
export const selectLang = (state) => state?.lang?.current ?? DEFAULT_LANG;

export const useLang = () => {
  const lang = useSelector(selectLang);
  const dispatch = useDispatch();
  const setLang = useCallback(
    (next) => {
      if (isSupportedLang(next)) dispatch(setLangAction(next));
    },
    [dispatch]
  );
  return [lang, setLang];
};

// 컴포넌트용. 언어가 바뀌면 새 함수가 돌아와 리렌더된다.
export const useT = () => {
  const lang = useSelector(selectLang);
  return useCallback((key, params) => translate(lang, key, params), [lang]);
};

const applyLang = (lang) => {
  if (!isSupportedLang(lang) || lang === currentLang) return;
  currentLang = lang;
  saveStoredLang(lang);
  if (typeof document !== "undefined") document.documentElement.lang = lang;
};

// store.js 가 한 번 부른다. 초기값을 맞추고 이후 변경을 따라간다.
export const bindLangToStore = (store) => {
  const initial = selectLang(store.getState());
  currentLang = initial;
  if (typeof document !== "undefined") document.documentElement.lang = initial;
  store.subscribe(() => applyLang(selectLang(store.getState())));
};
